/**
 * Renders whatever modules contributed to a named place on a core screen.
 *
 * `<Slot name="user.detail.actions" props={{ user }} />` — typed by `SlotProps`,
 * so a slot hands its contributions exactly what it promised. Contributions
 * are filtered through `canAny` like a menu item: presentation only, the
 * server still checks whatever the component goes on to call.
 *
 * Renders nothing at all when nothing is contributed, so placing a slot costs a
 * screen nothing.
 */
import type { ComponentType } from 'react';
import { useAuth } from '../auth/useAuth';
import { useModules } from './useModules';
import type { SlotContribution, SlotName, SlotProps } from './types';

export interface SlotComponentProps<N extends SlotName> {
  name: N;
  props: SlotProps[N];
}

export function Slot<N extends SlotName>({ name, props }: SlotComponentProps<N>) {
  const modules = useModules();
  const { canAny } = useAuth();

  const contributions = modules.flatMap((module) =>
    ((module.slots?.[name] ?? []) as readonly SlotContribution<N>[])
      .filter((contribution) => !contribution.anyOf || canAny(...contribution.anyOf))
      .map((contribution) => ({ key: `${module.key}:${contribution.id}`, contribution })),
  );

  return (
    <>
      {contributions.map(({ key, contribution }) => {
        const Contribution = contribution.component as ComponentType<SlotProps[N]>;
        return <Contribution key={key} {...props} />;
      })}
    </>
  );
}
